import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { resolveAssetUrl } from '../lib/assets';
import { regionLabel } from '../lib/regionNames';
import { CLASSIFIED_CATEGORY_MAP, resolveCat, formatClassifiedPrice } from '../data/classifiedsCategories';
import { ArrowLeft, Eye, MapPin, Phone, MessageSquare, Edit, Trash2, Truck, Store, CheckCircle2, Loader2, Share2, ImageOff, ShoppingCart, Plus, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import ClassifiedPurchaseDialog from '../components/ClassifiedPurchaseDialog';
import { TrustedSellerBadge } from '../components/ReputationBadge';
import { addToClsCart, replaceClsCart, removeFromClsCart, isInClsCart } from '../lib/classifiedsCart';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClassifiedDetailPage() {
  const { id } = useParams();
  const { tr } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [sellerTrusted, setSellerTrusted] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/classifieds/${id}`).then(({ data }) => { setItem(data); setActiveImg(0); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [id]);

  useEffect(() => {
    if (!item?.owner_id) { setSellerTrusted(false); return; }
    axios.get(`${API}/reputation/${item.owner_id}`)
      .then(({ data }) => setSellerTrusted(!!data.trusted_seller))
      .catch(() => setSellerTrusted(false));
  }, [item?.owner_id]);

  const isOwner = user && item && (user._id === item.owner_id || user.id === item.owner_id);

  const sendMessage = async () => {
    if (!user) { navigate('/login'); return; }
    if (!msg.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { recipient_id: item.owner_id, listing_id: item.id, listing_title: item.title, content: msg.trim() });
      setMsg(''); setMsgOpen(false);
      toast.success(tr('Ziņa nosūtīta!', 'Message sent!', 'Сообщение отправлено!'));
    } catch { toast.error(tr('Kļūda', 'Error', 'Ошибка')); }
    setSending(false);
  };

  const setStatus = async (status) => {
    setBusy(true);
    try { const { data } = await axios.patch(`${API}/classifieds/${item.id}/status`, { status }); setItem(data); toast.success(status === 'sold' ? tr('Atzīmēts kā pārdots', 'Marked as sold', 'Отмечено как продано') : tr('Aktivizēts', 'Reactivated', 'Активировано')); }
    catch { toast.error(tr('Kļūda', 'Error', 'Ошибка')); }
    setBusy(false);
  };

  const del = async () => {
    if (!window.confirm(tr('Dzēst sludinājumu neatgriezeniski?', 'Delete listing permanently?', 'Удалить объявление навсегда?'))) return;
    setBusy(true);
    try { await axios.delete(`${API}/classifieds/${item.id}`); toast.success(tr('Dzēsts', 'Deleted', 'Удалено')); navigate('/mani-sludinajumi'); }
    catch { toast.error(tr('Kļūda', 'Error', 'Ошибка')); setBusy(false); }
  };

  const share = async () => {
    const url = window.location.href;
    try { if (navigator.share) await navigator.share({ title: item.title, url }); else { await navigator.clipboard.writeText(url); toast.success(tr('Saite nokopēta', 'Link copied', 'Ссылка скопирована')); } } catch {}
  };

  const addToCart = () => {
    if (!user) { navigate('/login'); return; }
    const payload = { id: item.id, sellerId: item.owner_id, sellerName: item.owner_name, title: item.title, price: item.price, image: (item.images || [])[0], slug: item.slug };
    const res = addToClsCart(payload);
    if (res.ok && res.already) { toast(tr('Jau ir grozā', 'Already in cart', 'Уже в корзине')); return; }
    if (!res.ok && res.reason === 'different_seller') {
      if (window.confirm(tr('Grozā jau ir preces no cita pārdevēja. Aizstāt grozu ar šo preci?', 'Your cart has items from another seller. Replace the cart with this item?', 'В корзине товары другого продавца. Заменить?'))) {
        replaceClsCart(payload);
        toast.success(tr('Pievienots grozam', 'Added to cart', 'Добавлено в корзину'));
      }
      return;
    }
    toast.success(tr('Pievienots grozam', 'Added to cart', 'Добавлено в корзину'));
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]"><Loader2 className="w-8 h-8 animate-spin text-[#2F7E79]" /></div>;
  if (!item) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAFA] px-5 text-center">
      <div className="text-5xl mb-3">🧳</div>
      <p className="text-[#5C5C5C] mb-4">{tr('Sludinājums nav atrasts vai nav vairs pieejams.', 'Listing not found or no longer available.', 'Объявление не найдено.')}</p>
      <Button onClick={() => navigate('/sludinajumi')} className="bg-[#2F7E79] hover:bg-[#276b66] text-white rounded-full">{tr('Uz sludinājumiem', 'Browse listings', 'К объявлениям')}</Button>
    </div>
  );

  const cat = resolveCat(item.category);
  const pathIds = (item.category_path && item.category_path.length) ? item.category_path : (item.category ? [item.category] : []);
  const images = item.images || [];
  const f = item.fulfillment || {};

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="classified-detail-page">
      <div className="max-w-5xl mx-auto px-5 py-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-5" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> {tr('Atpakaļ', 'Back', 'Назад')}
        </button>

        {item.status === 'pending' && (
          <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2.5 text-sm font-semibold flex items-center gap-2" data-testid="pending-banner">
            <Loader2 className="w-4 h-4" /> {tr('Gaida jaunās kategorijas apstiprināšanu — tiks publicēts pēc admina apstiprinājuma.', 'Waiting for the new category to be approved — publishes after admin approval.', 'Ожидает одобрения новой категории — будет опубликовано после подтверждения.')}
          </div>
        )}

        {item.status === 'sold' && (
          <div className="mb-4 rounded-xl bg-[#121212] text-white px-4 py-2.5 text-sm font-semibold flex items-center gap-2" data-testid="sold-banner">
            <CheckCircle2 className="w-4 h-4" /> {tr('Šis sludinājums ir atzīmēts kā PĀRDOTS', 'This listing is marked as SOLD', 'Это объявление отмечено как ПРОДАНО')}
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Gallery */}
          <div className="lg:col-span-3">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-[#F3F2EE] border border-[#E5E4E0]" data-testid="classified-main-image">
              {images.length ? <img src={resolveAssetUrl(images[activeImg])} alt={item.title} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-[#C7C5BF]"><ImageOff className="w-10 h-10" /></div>}
            </div>
            {images.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${i === activeImg ? 'border-[#2F7E79]' : 'border-transparent'}`}
                    data-testid={`thumb-${i}`}>
                    <img src={resolveAssetUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5">
              {pathIds.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="classified-breadcrumb">
                  {pathIds.map((cid, i) => {
                    const node = resolveCat(cid);
                    return (
                      <React.Fragment key={cid}>
                        {i > 0 && <span className="text-[#C7C5BF] text-xs">›</span>}
                        <button onClick={() => navigate(i === 0 ? `/sludinajumi?category=${cid}` : `/sludinajumi?category=${pathIds[0]}&subcat=${cid}`)}
                          className="inline-flex items-center gap-1 text-xs font-semibold bg-[#E6F2F0] text-[#2F7E79] rounded-full px-2.5 py-1 hover:bg-[#d4ebe8] transition-colors">
                          {i === 0 && <span>{cat.emoji}</span>} {tr(node.lv, node.en, node.ru)}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
              <h1 className="font-outfit text-2xl font-bold text-[#121212] leading-snug" data-testid="classified-title">{item.title}</h1>
              <div className="mt-3 text-3xl font-bold text-[#2F7E79]" data-testid="classified-price">{formatClassifiedPrice(item, tr)}</div>
              <div className="mt-3 flex items-center gap-4 text-sm text-[#8A8A8A]">
                {item.region && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {item.location_name || regionLabel(item.region)}</span>}
                <span className="inline-flex items-center gap-1"><Eye className="w-4 h-4" /> {item.views || 0}</span>
              </div>
            </div>

            {/* Fulfilment */}
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5 space-y-2 text-sm" data-testid="classified-fulfillment">
              <div className={`flex items-center gap-2 ${f.pickup ? 'text-[#121212]' : 'text-[#C7C5BF] line-through'}`}><Store className="w-4 h-4" /> {tr('Savākšana uz vietas', 'Pickup', 'Самовывоз')}</div>
              <div className={`flex items-center gap-2 ${f.delivery ? 'text-[#121212]' : 'text-[#C7C5BF] line-through'}`}><Truck className="w-4 h-4" /> {tr('Piegāde pieejama', 'Delivery available', 'Доставка доступна')}</div>
              {f.location_text && <div className="flex items-center gap-2 text-[#5C5C5C] pt-1"><MapPin className="w-4 h-4" /> {f.location_text}</div>}
            </div>

            {/* Buy / escrow (non-owner, active only) */}
            {!isOwner && item.status === 'active' && (
              <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5 space-y-3" data-testid="classified-buy-card">
                {item.price_type === 'fixed' && typeof item.price === 'number' ? (
                  <>
                    <div className="flex items-start gap-2 text-xs text-[#2F7E79]">
                      <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {tr('Drošs darījums caur KoLV — nauda tiek turēta, līdz apstiprini preces saņemšanu.', 'Secure KoLV escrow — funds held until you confirm receipt.', 'Безопасная сделка KoLV — деньги удерживаются до подтверждения.')}
                    </div>
                    <Button onClick={() => { if (!user) { navigate('/login'); return; } setPurchaseOpen(true); }}
                      className="w-full h-11 rounded-full bg-[#D97736] hover:bg-[#c3652b] text-white font-semibold gap-2" data-testid="buy-now-btn">
                      <ShoppingCart className="w-4 h-4" /> {tr('Pirkt tagad', 'Buy now', 'Купить сейчас')} · €{item.price.toFixed(2)}
                    </Button>
                    <Button variant="outline" onClick={addToCart}
                      className="w-full h-11 rounded-full border-[#2F7E79] text-[#2F7E79] hover:bg-[#E6F2F0] gap-2" data-testid="add-to-cart-btn">
                      <Plus className="w-4 h-4" /> {isInClsCart(item.id) ? tr('Grozā', 'In cart', 'В корзине') : tr('Pievienot grozam', 'Add to cart', 'В корзину')}
                    </Button>
                    <p className="text-[11px] text-[#8A8A8A] text-center">{tr('KoLV komisija 10% tiek ieturēta no pārdevēja pēc darījuma.', 'KoLV takes a 10% fee from the seller after the deal.', 'KoLV удерживает 10% с продавца.')}</p>
                  </>
                ) : (
                  <div className="text-sm text-[#5C5C5C] flex items-start gap-2" data-testid="negotiable-note">
                    <MessageSquare className="w-4 h-4 mt-0.5 text-[#2F7E79] flex-shrink-0" />
                    {item.price_type === 'swap'
                      ? tr('Šis ir maiņas sludinājums — vienojies ar pārdevēju caur ziņām.', 'This is a swap listing — arrange with the seller via chat.', 'Это обмен — договоритесь через чат.')
                      : tr('Cena ir runājama — vienojies ar pārdevēju caur ziņām. Pārdevējs var nosūtīt rēķinu apmaksai čatā.', 'Price is negotiable — chat with the seller. They can send a payable invoice in chat.', 'Цена договорная — договоритесь в чате. Продавец может выставить счёт.')}
                  </div>
                )}
              </div>
            )}

            {/* Contact / owner actions */}
            {isOwner ? (
              <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5 space-y-2" data-testid="owner-actions">
                <p className="text-sm text-[#5C5C5C] mb-1">{tr('Tavs sludinājums', 'Your listing', 'Ваше объявление')}</p>
                <Button variant="outline" onClick={() => navigate(`/sludinajumi/${item.id}/labot`)} className="w-full gap-2 border-[#E5E4E0]" data-testid="edit-btn"><Edit className="w-4 h-4" /> {tr('Rediģēt', 'Edit', 'Редактировать')}</Button>
                {item.status === 'active'
                  ? <Button variant="outline" onClick={() => setStatus('sold')} disabled={busy} className="w-full gap-2 border-[#E5E4E0]" data-testid="mark-sold-btn"><CheckCircle2 className="w-4 h-4" /> {tr('Atzīmēt kā pārdotu', 'Mark as sold', 'Отметить проданным')}</Button>
                  : <Button variant="outline" onClick={() => setStatus('active')} disabled={busy} className="w-full gap-2 border-[#E5E4E0]" data-testid="reactivate-btn">{tr('Aktivizēt atkārtoti', 'Reactivate', 'Активировать')}</Button>}
                <Button variant="outline" onClick={del} disabled={busy} className="w-full gap-2 border-red-200 text-red-600 hover:bg-red-50" data-testid="delete-btn"><Trash2 className="w-4 h-4" /> {tr('Dzēst', 'Delete', 'Удалить')}</Button>
              </div>
            ) : (
              <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5 space-y-3" data-testid="contact-seller">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#E6F2F0] flex items-center justify-center text-[#2F7E79] font-bold">{(item.owner_name || '?').charAt(0).toUpperCase()}</div>
                  <div>
                    <p className="font-semibold text-[#121212]">{item.owner_name || tr('Pārdevējs', 'Seller', 'Продавец')}</p>
                    <button onClick={() => navigate(`/lietotajs/${item.owner_id}`)} className="text-xs text-[#2F7E79] hover:underline" data-testid="view-seller-btn">{tr('Skatīt profilu', 'View profile', 'Профиль')}</button>
                  </div>
                </div>
                {sellerTrusted && <TrustedSellerBadge size="sm" />}
                {item.contact_phone && (
                  <a href={`tel:${item.contact_phone}`} className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#2F7E79] hover:bg-[#276b66] text-white font-semibold transition-colors" data-testid="call-seller-btn">
                    <Phone className="w-4 h-4" /> {item.contact_phone}
                  </a>
                )}
                <Button variant="outline" className="w-full gap-2 border-[#2F7E79] text-[#2F7E79] hover:bg-[#E6F2F0]"
                  onClick={() => { if (!user) { navigate('/login'); return; } navigate(`/messages?to=${item.owner_id}&name=${encodeURIComponent(item.owner_name || '')}`); }}
                  data-testid="message-seller-btn">
                  <MessageSquare className="w-4 h-4" /> {tr('Rakstīt pārdevējam', 'Message seller', 'Написать продавцу')}
                </Button>
                <button onClick={share} className="w-full inline-flex items-center justify-center gap-2 text-sm text-[#5C5C5C] hover:text-[#121212] pt-1" data-testid="share-btn"><Share2 className="w-4 h-4" /> {tr('Dalīties', 'Share', 'Поделиться')}</button>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {item.description && (
          <div className="mt-6 bg-white border border-[#E5E4E0] rounded-2xl p-6">
            <h2 className="font-outfit font-semibold text-[#121212] mb-2">{tr('Apraksts', 'Description', 'Описание')}</h2>
            <p className="text-[#5C5C5C] leading-relaxed whitespace-pre-line" data-testid="classified-description">{item.description}</p>
          </div>
        )}
      </div>

      <ClassifiedPurchaseDialog
        open={purchaseOpen} onOpenChange={setPurchaseOpen} ids={[item.id]}
        onPaid={() => { removeFromClsCart(item.id); navigate('/sludinajumi/darijumi'); }} />
    </div>
  );
}
